import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * ProPinShowcase — homepage strip of real Pro trucks' logo pins.
 *
 * Renders the EXACT pin treatment used on the live map (single 2px
 * orange ring, 44px outer / 40px inner, `objectPosition` honoring the
 * owner's logo_pin_pos focal point) — so visitors who scroll past the
 * mini-map see what their own pin would look like, side-by-side with
 * actual Pro trucks. Doubles as social proof.
 *
 * Behavior:
 *   - Renders nothing if the API returns an empty pool (avoids an
 *     awkward empty section when the install has no Pro pins yet).
 *   - Each pin links to the truck's `/t/:slug` profile when published;
 *     unpublished trucks render as a non-clickable pin so we still
 *     show pool size without leaking 404s.
 *   - Below the row sits a small "Pro upgrade" CTA targeting Free owners.
 */
export default function ProPinShowcase() {
  const [trucks, setTrucks] = useState(null); // null = loading, [] = no data
  const [pool, setPool] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/landing/pro-pin-showcase", { params: { limit: 6 } })
      .then(({ data }) => {
        if (cancelled) return;
        setTrucks(Array.isArray(data?.trucks) ? data.trucks : []);
        setPool(Number(data?.pool_size) || 0);
      })
      .catch(() => !cancelled && setTrucks([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // Loading / empty state — render nothing rather than a skeleton row.
  // The hero section already gives the page enough above-the-fold mass.
  if (trucks === null) return null;
  if (trucks.length === 0) return null;

  return (
    <section
      className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24"
      data-testid="pro-pin-showcase"
    >
      <div className="rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/5 via-amber-500/5 to-transparent p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
              <Sparkle weight="fill" className="h-3.5 w-3.5" /> Pro pins on the map today
            </span>
            <h3 className="mt-2 font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight">
              Make your truck the one customers tap first.
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-xl">
              Pro trucks show their own logo right on the map — not a generic icon.
              {pool > trucks.length ? ` ${pool} trucks are doing it right now.` : ""}
            </p>
          </div>
          <Link
            to="/upgrade"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 shrink-0"
            data-testid="pro-pin-showcase-upgrade-cta"
          >
            Become a Pro
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Pin row — flex so it wraps cleanly on narrow screens. */}
        <div className="mt-5 flex flex-wrap items-end gap-x-5 gap-y-4">
          {trucks.map((t) => (
            <ShowcasePin key={t.truck_id} truck={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowcasePin({ truck }) {
  // Same visual treatment as `.truck-logo-pin` on the live map — replicated
  // in inline styles so this section is self-contained (no dependency on
  // global classes that might evolve and break the showcase).
  const x = truck.logo_pin_pos?.x ?? 50;
  const y = truck.logo_pin_pos?.y ?? 50;
  const inner = (
    <div className="flex flex-col items-center gap-1.5 group">
      <div
        className="h-12 w-12 rounded-full bg-white grid place-items-center overflow-hidden p-[1px] transition-transform duration-200 group-hover:scale-110"
        style={{
          boxShadow: "0 0 0 2px #ea580c, 0 4px 12px rgba(15, 23, 42, 0.22)",
        }}
        data-testid="pro-pin-showcase-pin"
      >
        <img
          src={truck.logo_url}
          alt={`${truck.name} logo`}
          loading="lazy"
          className="h-[44px] w-[44px] rounded-full object-cover"
          style={{ objectPosition: `${x}% ${y}%` }}
        />
      </div>
      <div className="text-[10px] font-semibold text-muted-foreground max-w-[80px] truncate text-center">
        {truck.name}
      </div>
    </div>
  );
  if (truck.slug) {
    return (
      <Link
        to={`/t/${truck.slug}`}
        aria-label={`View ${truck.name}'s profile`}
        className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl"
        data-testid="pro-pin-showcase-link"
      >
        {inner}
      </Link>
    );
  }
  return inner;
}
